import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Menu, Search } from 'lucide-react';

import { usePageHeader } from '@/contexts/PageHeaderContext';
import { ParkingSessionDetailsQuery } from '@/pages/parking-sessions/parking-session-details-query';
import { AlertsDropdown } from '../alerts';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { LanguageSelector } from '../ui/language-selector';
import { ParkingSessionAutocomplete } from '../ui/parking-session-autocomplete';
import { ThemeToggle } from '../ui/theme-toggle';
import { HeaderSearch } from './header-search';
import type { MenuItem } from './sidebar';

interface HeaderProps {
	onMenuClick: () => void;
	isCollapsed?: boolean;
	menuItems: MenuItem[];
}

// Route-based header configuration
const getHeaderFromPath = (pathname: string, t: any) => {
	const segments = pathname.split('/').filter(Boolean);

	if (segments.length === 0) {
		return {
			title: t('dashboard:title', 'Dashboard'),
			description: t(
				'dashboard:subtitle',
				'Monitor and manage your system'
			),
			showBack: false
		};
	}

	const resource = segments[0];
	const action = segments[1];

	// For Create/Edit actions, show the parent resource header (not the action header)
	if (action === 'create' || action === 'edit') {
		// Show the parent resource list header
		const resourceTitle = t(`navigation:resources.${resource}.title`, '');
		const resourceDescription = t(
			`navigation:resources.${resource}.description`,
			''
		);

		if (resourceTitle && resourceDescription) {
			return {
				title: resourceTitle,
				description: resourceDescription,
				showBack: false
			};
		}

		// Fallback if no translation found
		const resourceName = resource
			.replace(/-/g, ' ')
			.replace(/\b\w/g, (l: string) => l.toUpperCase());
		return {
			title: resourceName,
			description: '',
			showBack: false
		};
	}

	// List page - use translations if available
	const resourceTitle = t(`navigation:resources.${resource}.title`, '');
	const resourceDescription = t(
		`navigation:resources.${resource}.description`,
		''
	);

	if (resourceTitle && resourceDescription) {
		return {
			title: resourceTitle,
			description: resourceDescription,
			showBack: false
		};
	}

	// Default
	return {
		title: 'Dashboard',
		description: 'Monitor and manage your system',
		showBack: false
	};
};

export const Header: React.FC<HeaderProps> = ({
	onMenuClick,
	isCollapsed: _isCollapsed = false,
	menuItems
}) => {
	const location = useLocation();
	const { t } = useTranslation(['navigation', 'common']);
	const pageHeaderContext = usePageHeader();
	const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
		null
	);
	const [searchSessionId, setSearchSessionId] = useState('');

	// Use PageHeaderContext if available, otherwise fall back to route-based logic
	const { showBack } = useMemo(() => {
		// Check if we're on an edit or create route
		const isEditOrCreateRoute = /\/(edit|create)(\?|$)/.test(
			location.pathname
		);

		// For edit/create routes, use route-based title but respect back button from context
		if (isEditOrCreateRoute) {
			const fallback = getHeaderFromPath(location.pathname, t);
			return {
				...fallback,
				showBack: pageHeaderContext.showBackButton || false
			};
		}

		// If PageHeaderContext has a title set, use it (for list pages)
		if (pageHeaderContext.title) {
			return {
				title: pageHeaderContext.title,
				description: pageHeaderContext.description || '',
				showBack: pageHeaderContext.showBackButton || false,
				backTo: undefined
			};
		}
		// Otherwise fall back to route-based logic
		const fallback = getHeaderFromPath(location.pathname, t);
		return fallback;
	}, [
		location.pathname,
		pageHeaderContext.title,
		pageHeaderContext.description,
		pageHeaderContext.showBackButton,
		t
	]);

	const handleBack = () => {
		if (pageHeaderContext.onBackClick) {
			pageHeaderContext.onBackClick();
		}
	};

	const renderParkingSessionSearch = (className?: string) => (
		<ParkingSessionAutocomplete
			value={searchSessionId}
			onChange={id => {
				setSearchSessionId(id);
				if (id) {
					setSelectedSessionId(id);
				}
			}}
			placeholder={t('common:globalSearch.placeholder')}
			className={className}
			inputClassName="h-9 text-sm"
			leadingIcon={<Search className="h-4 w-4" />}
		/>
	);

	return (
		<div className="bg-transparent">
			<div className="w-full px-4">
				<header className="py-5 border-gray-200 dark:border-[#334155] border-b dark:border-none">
					<div className="flex flex-col gap-4">
						<div className="flex flex-wrap items-start justify-between gap-3">
							<div className="flex items-center gap-2 min-w-0 flex-1">
								{/* Mobile menu button */}
								<button
									onClick={onMenuClick}
									className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors mr-3 dark:text-[#94A3B8] dark:hover:text-[#F1F5F9] dark:hover:bg-[rgba(51,65,85,0.5)]"
									title="Open menu"
								>
									<Menu className="w-5 h-5" />
								</button>

								{/* Show HeaderSearch with clickable title navigation on all screen sizes */}
								<HeaderSearch
									menuItems={menuItems}
									showBack={showBack}
									onBack={handleBack}
								/>
							</div>

							<div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
								<div className="hidden md:block w-[180px] lg:w-[220px]">
									{renderParkingSessionSearch('w-full')}
								</div>
								<AlertsDropdown />
								<LanguageSelector />
								<ThemeToggle />
							</div>
						</div>

						<div className="w-full md:hidden">
							{renderParkingSessionSearch('w-full')}
						</div>
					</div>
				</header>
			</div>
			<Dialog
				open={Boolean(selectedSessionId)}
				onOpenChange={() => setSelectedSessionId(null)}
			>
				<DialogContent size="lg" enablePreRender>
					<DialogHeader>
						<DialogTitle>
							{t('common:labels.view_details')}
						</DialogTitle>
					</DialogHeader>
					{selectedSessionId ? (
						<ParkingSessionDetailsQuery
							parkingSessionId={selectedSessionId}
						/>
					) : null}
				</DialogContent>
			</Dialog>
		</div>
	);
};
